import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { pageTemplates } from "../utils/pageTemplates";
import { SeoContentSection } from "../components/Seo";
import { KONTAKT_INTRO, KONTAKT_FAQ } from "../data/homeContent";
import { INK, PRIMARY, Overline, AuroraBlue, AuroraLight, Reveal, MagneticButton } from "../components/shared/kursusUi";

const getSection = (sections, type) => sections?.find(s => s.type === type);

const ContactPage = () => {
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/kontakt`);
      setPageData(response.data);
    } catch (error) {
      setPageData(pageTemplates["kontakt"]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error("Udfyld venligst navn, email og besked");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/submit`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      toast.success("Tak! Din henvendelse er modtaget.");
    } catch (error) {
      toast.error("Kunne ikke sende henvendelsen. Prøv igen eller skriv direkte til kundeservice@votra.dk");
    } finally {
      setSubmitting(false);
    }
  };

  const template = pageTemplates["kontakt"];
  const data = pageData || template;
  // Per-section field-level fallback: merge DB section over template section,
  // so any missing/empty fields fall back to template defaults
  const mergeSection = (type) => {
    const dbSection = getSection(data.sections, type) || {};
    const templateSection = getSection(template.sections, type) || {};
    return { ...templateSection, ...Object.fromEntries(
      Object.entries(dbSection).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    ) };
  };
  const hero = mergeSection('hero');
  const contactInfo = mergeSection('contactInfo');
  const form = mergeSection('form');

  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(210,100%,50%)] focus-visible:border-transparent transition-shadow";


  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'hsl(210, 20%, 98%)' }}>
      <div className="votra-grain" aria-hidden />
      <Header />

      {/* Hero — dark ink + blue aurora */}
      <section className="hero-light relative overflow-hidden" style={{ backgroundColor: '#F4F6FB' }}>
        <AuroraLight />
        <div className="fx-scan"><span /></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24 text-center">
          <Reveal className="flex justify-center"><Overline light>Kontakt</Overline></Reveal>
          <Reveal i={1}>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-white leading-tight mb-5">Kontakt os</h1>
          </Reveal>
          <Reveal i={2}>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Vi er her for at hjælpe dig. Skriv til os på email, det er den hurtigste vej til svar.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
        <div className="max-w-4xl mx-auto">
          {/* USP: lange åbningstider */}
          <Reveal className="mb-10 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4 border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="opening-hours-usp">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <Clock className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg md:text-xl font-medium text-slate-900">Blandt Danmarks mest fleksible åbningstider</h3>
              <p className="text-slate-600 text-sm md:text-base">
                Vi har åbent <b className="text-slate-900">{contactInfo?.hours?.weekdays || 'hverdage 07:30-23:30'}</b> og <b className="text-slate-900">{contactInfo?.hours?.weekend || 'weekend 08:00-23:30'}</b>, så du kan få hjælp, når det passer dig.
              </p>
            </div>
          </Reveal>

          {contactInfo && (
            <div className="space-y-6 mb-14">
              {/* Primary Contact: Email — premium ink card */}
              <Reveal className="rounded-3xl p-8 md:p-10 text-white shadow-xl relative overflow-hidden" style={{ backgroundColor: INK }}>
                <AuroraBlue />
                <div className="absolute top-5 right-5 z-10">
                  <span className="px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wide">
                    Primær kontakt
                  </span>
                </div>
                <div className="flex items-start gap-5 relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-300 mb-1">
                      {contactInfo.email?.label || 'Email'}
                    </h3>
                    <a
                      href={`mailto:${contactInfo.email?.value}`}
                      className="font-display text-2xl md:text-3xl font-medium hover:underline break-all text-white"
                    >
                      {contactInfo.email?.value}
                    </a>
                    <p className="text-sm text-slate-300 mt-2">{contactInfo.email?.note}</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Vi anbefaler email som hurtigste vej til svar, så har du også vores svar på skrift.
                    </p>
                  </div>
                </div>
              </Reveal>

              {/* Secondary Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Reveal i={0} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,40,90,0.12)]">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'hsl(24, 94%, 50%, 0.1)' }}>
                    <Phone className="h-6 w-6" style={{ color: 'hsl(24, 94%, 50%)' }} />
                  </div>
                  <h3 className="font-semibold mb-2 text-slate-900">{contactInfo.phone?.label || 'Telefon'}</h3>
                  <a href={`tel:${contactInfo.phone?.value?.replace(/\s/g, '')}`} className="text-[hsl(210,100%,45%)] font-medium hover:underline">
                    {contactInfo.phone?.value}
                  </a>
                  <p className="text-sm text-slate-500 mt-1">{contactInfo.phone?.hours}</p>
                </Reveal>

                <Reveal i={1} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,40,90,0.12)]">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'hsl(210,100%,45%,0.1)' }}>
                    <MapPin className="h-6 w-6" style={{ color: PRIMARY }} />
                  </div>
                  <h3 className="font-semibold mb-2 text-slate-900">{contactInfo.address?.label || 'Adresse'}</h3>
                  <p className="text-slate-700">{contactInfo.address?.line1}</p>
                  <p className="text-slate-700">{contactInfo.address?.line2}</p>
                </Reveal>

                <Reveal i={2} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-[0_8px_30px_rgba(15,40,90,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(15,40,90,0.12)]">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'hsl(210,100%,45%,0.1)' }}>
                    <Clock className="h-6 w-6" style={{ color: PRIMARY }} />
                  </div>
                  <h3 className="font-semibold mb-2 text-slate-900">{contactInfo.hours?.label || 'Åbningstider'}</h3>
                  <p className="text-slate-700">{contactInfo.hours?.weekdays}</p>
                  <p className="text-slate-700">{contactInfo.hours?.weekend}</p>
                </Reveal>
              </div>
            </div>
          )}

          {/* Google Maps — sådan finder du os */}
          <Reveal className="mb-14" data-testid="contact-map-section">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5" style={{ color: PRIMARY }} />
              <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900">Sådan finder du os</h3>
            </div>
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-[0_8px_30px_rgba(15,40,90,0.08)]">
              <iframe
                title="Votra på kort"
                data-testid="contact-map-iframe"
                src={`https://maps.google.com/maps?q=${encodeURIComponent([contactInfo?.address?.line1, contactInfo?.address?.line2].filter(Boolean).join(', ') || 'Sct. Hansgade 9, 4100 Ringsted')}&z=15&output=embed`}
                width="100%"
                height="360"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([contactInfo?.address?.line1, contactInfo?.address?.line2].filter(Boolean).join(', ') || 'Sct. Hansgade 9, 4100 Ringsted')}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-[hsl(210,100%,45%)] hover:underline"
              data-testid="contact-map-directions"
            >
              <MapPin className="h-4 w-4" /> Få rutevejledning i Google Maps
            </a>
          </Reveal>

          {form && (
            <Reveal className="kursus-glass rounded-3xl p-8 md:p-10" data-testid="contact-form-card">
              <Overline>Skriv til os</Overline>
              <h3 className="font-display text-2xl md:text-3xl font-medium mb-6 text-slate-900">
                {form.title}
              </h3>
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-14 w-14 mx-auto mb-4 text-emerald-500" />
                  <h4 className="text-lg font-semibold text-slate-800 mb-2">Tak for din henvendelse!</h4>
                  <p className="text-slate-600 mb-6">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="text-[hsl(210,100%,45%)] font-medium hover:underline"
                    data-testid="send-another-message-btn"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        {form.fields?.name?.label || 'Navn'} *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={inputCls}
                        placeholder={form.fields?.name?.placeholder || 'Dit navn'}
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        {form.fields?.email?.label || 'Email'} *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={inputCls}
                        placeholder={form.fields?.email?.placeholder || 'din@email.dk'}
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {form.fields?.subject?.label || 'Emne'}
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className={inputCls}
                      placeholder={form.fields?.subject?.placeholder || 'Hvad handler det om?'}
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {form.fields?.message?.label || 'Besked'} *
                    </label>
                    <textarea
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className={inputCls}
                      placeholder={form.fields?.message?.placeholder || 'Skriv din besked her...'}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <MagneticButton
                    type="submit"
                    disabled={submitting}
                    className="kursus-glow-btn w-full py-3.5 rounded-full font-semibold text-white transition-colors disabled:opacity-60"
                    style={{ backgroundColor: PRIMARY }}
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? 'Sender...' : (form.buttonText || 'Send besked')}
                  </MagneticButton>
                </form>
              )}
            </Reveal>
          )}
        </div>
      </div>

      <SeoContentSection intro={KONTAKT_INTRO} faq={KONTAKT_FAQ} testid="kontakt-seo-content" />

      <Footer />
    </div>
  );
};

export default ContactPage;
